// pricing-tax-versions — client-side helpers for a tourist tax's scheduled
// rate changes ("rate_versions" on the Alpine tax row `tx`). A version
// replaces ALL rate values for every night on/after its `effective_from`
// until the next version; the row's own values (the "base") apply before
// the first date. Model, price base, exemptions, caps etc. are shared.
//
// Pure functions, registered as the `$taxVersions` magic in
// alpine-core-registrations.ts. Dates are ISO `YYYY-MM-DD` strings compared
// lexically; ALL date math is UTC (never local Date getters) so the result
// never depends on the browser's time zone. Per-version validity (blank
// active value, seasonal overlap, bracket issues) lives in the Alpine root
// because it needs `$taxBrackets` and the seasonal guards.

export interface RateVersion {
  effective_from?: string;
  [k: string]: unknown;
}

export interface VersionedTax {
  model?: string;
  rate_versions?: RateVersion[];
  [k: string]: unknown;
}

export type VersionStatus = "now" | "planned" | "expired";

export interface VersionEntry {
  /** null = the base values; otherwise the index into rate_versions. */
  idx: number | null;
  kind: "until" | "from" | "since";
  date: string;
  status: VersionStatus;
}

/** The rate keys a version carries (the row's own keys of the same name). */
export const RATE_KEYS: readonly string[] = [
  "percent",
  "flat_amount_euros",
  "seasonal_rates",
  "brackets",
  "bracket_top",
  "bracket_top_amount_euros",
  "escalation_every_euros",
  "escalation_add_euros",
  "escalation_max_euros",
];

const MODEL_KEYS: Record<string, readonly string[]> = {
  percent_of_rate: ["percent", "seasonal_rates"],
  flat_per_person_night: ["flat_amount_euros", "seasonal_rates"],
  price_bracket: [
    "brackets",
    "bracket_top",
    "bracket_top_amount_euros",
    "escalation_every_euros",
    "escalation_add_euros",
    "escalation_max_euros",
  ],
};

const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function versions(tx: VersionedTax): RateVersion[] {
  return Array.isArray(tx?.rate_versions) ? tx.rate_versions : [];
}

function dateOf(v: RateVersion | undefined): string {
  return typeof v?.effective_from === "string" ? v.effective_from : "";
}

function clone<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

/** Index of the version valid on `date` (latest effective_from <= date), or null for the base. */
export function versionValidOn(tx: VersionedTax, date: string): number | null {
  let best: number | null = null;
  let bestDate = "";
  versions(tx).forEach((v, i) => {
    const d = dateOf(v);
    if (d !== "" && d <= date && d >= bestDate) {
      best = i;
      bestDate = d;
    }
  });
  return best;
}

export function currentVersionIdx(tx: VersionedTax, today: string): number | null {
  return versionValidOn(tx, today);
}

/**
 * The drawer's list rows: the base ("until" the day before the earliest
 * date) and one per version. A dateless version (a cleared date — invalid,
 * flagged elsewhere) is ignored for the base's end date and today's entry,
 * and is itself never "now" or "expired".
 */
export function versionEntries(tx: VersionedTax, today: string): VersionEntry[] {
  const vs = versions(tx);
  const cur = currentVersionIdx(tx, today);
  const curDate = cur === null ? "" : dateOf(vs[cur]);
  const first = vs.map(dateOf).filter((d) => d !== "").sort()[0] ?? "";
  const out: VersionEntry[] = [
    { idx: null, kind: "until", date: first === "" ? "" : shiftISO(first, -1), status: cur === null ? "now" : "expired" },
  ];
  vs.forEach((v, i) => {
    const d = dateOf(v);
    let status: VersionStatus = "planned";
    if (i === cur) status = "now";
    else if (d !== "" && cur !== null && d < curDate) status = "expired";
    out.push({ idx: i, kind: d !== "" && d <= today ? "since" : "from", date: d, status });
  });
  return out;
}

export function viewForToday(tx: VersionedTax, today: string): VersionedTax {
  const idx = currentVersionIdx(tx, today);
  if (idx === null) return { ...tx };
  const view: VersionedTax = { ...tx };
  copyRateValues(versions(tx)[idx], view);
  return view;
}

/** The value a rate key has when a version does not carry it (never the base's). */
function emptyRateValue(k: string): unknown {
  if (k === "seasonal_rates" || k === "brackets") return [];
  if (k === "bracket_top") return "flat";
  return "";
}

/** Copies (deep) every rate key of `from` onto `to`; a key `from` lacks gets its empty value. */
function copyRateValues(from: RateVersion, to: VersionedTax): void {
  for (const k of RATE_KEYS) to[k] = k in from ? clone(from[k]) : emptyRateValue(k);
}

export function nextVersions(tx: VersionedTax, today: string): RateVersion[] {
  return versions(tx)
    .filter((v) => dateOf(v) > today)
    .sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : dateOf(a) > dateOf(b) ? 1 : 0));
}

/** Adds a version dated `date`, copying (deep) the rate keys of the entry valid the day before, plus model. Returns the new index, or -1 for a blank or already-used date. */
export function addVersion(tx: VersionedTax, date: string): number {
  if (typeof date !== "string" || date === "") return -1;
  if (versions(tx).some((v) => dateOf(v) === date)) return -1;
  const srcIdx = versionValidOn(tx, shiftISO(date, -1));
  const src: VersionedTax | RateVersion = srcIdx === null ? tx : versions(tx)[srcIdx];
  const nv: RateVersion = { effective_from: date };
  for (const k of RATE_KEYS) if (k in src) nv[k] = clone(src[k]);
  nv.model = tx.model;
  if (!Array.isArray(tx.rate_versions)) tx.rate_versions = [];
  tx.rate_versions.push(nv);
  tx.rate_versions.sort((a, b) => (dateOf(a) < dateOf(b) ? -1 : dateOf(a) > dateOf(b) ? 1 : 0));
  return tx.rate_versions.indexOf(nv);
}

/** Folds the first version into the base (copies its rate keys onto tx, removes it). */
export function promoteFirstVersion(tx: VersionedTax): void {
  const vs = versions(tx);
  if (vs.length === 0) return;
  copyRateValues(vs[0], tx);
  tx.rate_versions = vs.slice(1);
}

function blank(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

/** The main rate value of each model (what a model switch clears in a version). */
const ACTIVE_KEY: Record<string, string> = {
  percent_of_rate: "percent",
  flat_per_person_night: "flat_amount_euros",
  price_bracket: "brackets",
};

/**
 * Clears the active value of `model` in a version that came from another
 * model, so it reads as incomplete (and holds the autosave) until the
 * operator fills it: a percent left over as "0" from a flat/bracket render
 * would otherwise be a valid 0 % and silently undercharge from its date.
 */
function clearActiveValue(v: RateVersion, model: string): void {
  const k = ACTIVE_KEY[model];
  if (k === undefined) return;
  v[k] = k === "brackets" ? [{ up_to_euros: "", amount_euros: "" }] : "";
}

/**
 * Called after tx.model changed. A version that came from another model
 * (v.model !== tx.model) gets the new model's active value cleared; then
 * every version is seeded with the base's values for the keys of tx.model
 * that are still blank (seasonal list, bracket top, …) and takes tx.model.
 */
export function seedVersionsForModel(tx: VersionedTax): void {
  const model = String(tx.model ?? "");
  const keys = MODEL_KEYS[model] ?? [];
  for (const v of versions(tx)) {
    const fromOtherModel = v.model !== tx.model;
    if (fromOtherModel) clearActiveValue(v, model);
    for (const k of keys) {
      // A cleared active value stays blank — seeding it from the base (whose
      // own value is a leftover of the old model too) would hide it again.
      if (fromOtherModel && k === ACTIVE_KEY[model]) continue;
      // An empty seasonal list is a valid "no seasons", not a blank.
      const isBlank = k === "seasonal_rates" ? v[k] === undefined || v[k] === null : blank(v[k]);
      if (isBlank && k in tx) v[k] = clone(tx[k]);
    }
    v.model = tx.model;
  }
}

export function versionDatesInvalid(tx: VersionedTax): boolean {
  const seen = new Set<string>();
  for (const v of versions(tx)) {
    const d = dateOf(v);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || seen.has(d)) return true;
    seen.add(d);
  }
  return false;
}

function normalized(v: unknown): string {
  return JSON.stringify(v === undefined || v === null ? "" : v);
}

/** True when both objects carry identical values for every rate key. */
export function sameRateValues(a: object, b: object): boolean {
  const x = a as Record<string, unknown>;
  const y = b as Record<string, unknown>;
  return RATE_KEYS.every((k) => normalized(x[k]) === normalized(y[k]));
}

export function shiftISO(date: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return "";
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days);
  return new Date(t).toISOString().slice(0, 10);
}

export function formatISODate(date: string, lang: "de" | "en"): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  if (lang === "en") return `${Number(m[3])} ${MONTHS_EN[Number(m[2]) - 1]} ${m[1]}`;
  return `${m[3]}.${m[2]}.${m[1]}`;
}
